import { z } from 'zod'
import { api } from '@/shared/api/client'
import { uuidSchema } from '@/shared/api/schemas'

const requestSchema = z.object({
  id: uuidSchema,
  userId: uuidSchema,
  userName: z.string(),
  amount: z.number().int(),
  currency: z.string(),
  note: z.string().nullish(),
  state: z.enum(['PENDING', 'APPROVED', 'REJECTED']),
  approvedAmount: z.number().int().nullish(),
  decidedByName: z.string().nullish(),
  decidedAt: z.string().nullish(),
  decisionNote: z.string().nullish(),
  createdAt: z.string(),
})
export type CollectRequest = z.infer<typeof requestSchema>

const limitSchema = z.object({
  userId: uuidSchema,
  userName: z.string(),
  limited: z.boolean(),
  limit: z.number().int(),
  used: z.number().int(),
  remaining: z.number().int(),
  currency: z.string(),
  pending: requestSchema.nullish(),
})
export type CollectLimit = z.infer<typeof limitSchema>

const cashierSchema = z.object({ userId: uuidSchema, userName: z.string() })
export type Cashier = z.infer<typeof cashierSchema>

/** Xodimning pul yig'ish limiti (ADR-054) */
export async function fetchMyCollectLimit(signal?: AbortSignal): Promise<CollectLimit> {
  const response = await api.get<unknown>('/api/cashdesk/collect-limits/me', { ...(signal ? { signal } : {}) })
  return limitSchema.parse(response.data)
}

export async function fetchCollectLimits(signal?: AbortSignal): Promise<CollectLimit[]> {
  const response = await api.get<unknown>('/api/cashdesk/collect-limits', { ...(signal ? { signal } : {}) })
  return z.array(limitSchema).parse(response.data)
}

export async function fetchCollectRequests(state: string | null, signal?: AbortSignal): Promise<CollectRequest[]> {
  const response = await api.get<unknown>('/api/cashdesk/collect-limits/requests', {
    params: state ? { state } : {},
    ...(signal ? { signal } : {}),
  })
  return z.array(requestSchema).parse(response.data)
}

export async function fetchCollectCashiers(signal?: AbortSignal): Promise<Cashier[]> {
  const response = await api.get<unknown>('/api/cashdesk/collect-limits/cashiers', { ...(signal ? { signal } : {}) })
  return z.array(cashierSchema).parse(response.data)
}

export async function grantCollectCredit(userId: string, userName: string, amount: number, reason: string | null) {
  const response = await api.post<unknown>(`/api/cashdesk/collect-limits/${userId}/credits`, { userName, amount, reason })
  return limitSchema.parse(response.data)
}

export async function requestCollectCredit(amount: number, note: string | null): Promise<CollectRequest> {
  const response = await api.post<unknown>('/api/cashdesk/collect-limits/requests', { amount, note })
  return requestSchema.parse(response.data)
}

export async function approveCollectRequest(id: string, amount: number | null, note: string | null) {
  const response = await api.post<unknown>(`/api/cashdesk/collect-limits/requests/${id}/approve`, { amount, note })
  return requestSchema.parse(response.data)
}

export async function rejectCollectRequest(id: string, note: string | null) {
  const response = await api.post<unknown>(`/api/cashdesk/collect-limits/requests/${id}/reject`, { note })
  return requestSchema.parse(response.data)
}
